/**
 * Mini-charts SVG inline. Sin librería externa: 6 puntos no justifican
 * importar Recharts/Chart.js.
 *
 * Colores desde `lib/chartColors` (tokens de la paleta). Barras y trazos
 * planos, sin degradados (DESIGN.md §1 y §8).
 */

import { CHART_COLORS } from "../../lib/chartColors";
import type { PuntoTendencia } from "./api";

interface SparklineProps {
  /** Serie numérica a graficar. Mínimo 2 puntos para renderizar. */
  values: ReadonlyArray<number>;
  /** Color del trazo y del relleno. Default brand-600. */
  stroke?: string | undefined;
  /** Si se pinta el área bajo el trazo (color plano, opacidad baja). */
  fillBelow?: boolean | undefined;
  /** Clases Tailwind para sizing. */
  className?: string | undefined;
  /** Etiqueta de accesibilidad. */
  ariaLabel?: string | undefined;
}

/**
 * Sparkline mínima para el pie de una tarjeta KPI (la tarjeta es `relative
 * overflow-hidden` y la sparkline va absoluta al borde inferior).
 *
 * - viewBox 200x40 con `preserveAspectRatio="none"`: se estira al contenedor.
 * - `vectorEffect="non-scaling-stroke"` mantiene el trazo de 2px nítido.
 * - El relleno cierra en y=H+1 para tocar el borde de la tarjeta sin línea.
 */
export function Sparkline({
  values,
  stroke = CHART_COLORS.brand600,
  fillBelow = true,
  className = "h-12 w-full",
  ariaLabel = "Tendencia",
}: SparklineProps): JSX.Element | null {
  if (values.length < 2) return null;
  const W = 200;
  const H = 40;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = W / (values.length - 1);
  const PAD_Y = 4;
  const usableH = H - PAD_Y * 2;
  const points = values.map((v, i) => ({
    x: i * stepX,
    y: PAD_Y + usableH - ((v - min) / range) * usableH,
  }));
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");
  const last = points[points.length - 1]!;
  const first = points[0]!;
  const fillPath = `${linePath} L ${last.x.toFixed(2)},${H + 1} L ${first.x.toFixed(2)},${H + 1} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={className}
      role="img"
      aria-label={ariaLabel}
    >
      {fillBelow && <path d={fillPath} fill={stroke} fillOpacity={0.12} />}
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

interface ChartProps {
  puntos: PuntoTendencia[];
  formatValor?: (v: number) => string;
  /** Nombre del gráfico para lectores de pantalla. */
  titulo?: string | undefined;
}

// ViewBox fijo; el SVG se escala al ancho del contenedor (`w-full h-auto`).
// La tarjeta mide ~500 px en escritorio (dos por fila), así que una fuente de
// 15 unidades queda en ~12 px reales: el mínimo legible del admin.
const CHART_W = 600;
const CHART_H = 300;
const FONT = 15;
const PAD_L = 104;
const PAD_R = 16;
const PAD_T = 24;
const PAD_B = 48;

function rangoY(puntos: PuntoTendencia[]): { min: number; max: number } {
  if (puntos.length === 0) return { min: 0, max: 1 };
  const valores = puntos.map((p) => p.valor);
  const max = Math.max(...valores, 1);
  return { min: 0, max };
}

function ticksY(min: number, max: number): number[] {
  const span = max - min;
  if (span === 0) return [0];
  return [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(min + span * f));
}

function GrillaY({
  min,
  max,
  innerW,
  innerH,
  fmt,
}: {
  min: number;
  max: number;
  innerW: number;
  innerH: number;
  fmt: (v: number) => string;
}): JSX.Element {
  return (
    <>
      {ticksY(min, max).map((t, i) => {
        const y = PAD_T + innerH - ((t - min) / (max - min || 1)) * innerH;
        return (
          <g key={i}>
            <line
              x1={PAD_L}
              x2={PAD_L + innerW}
              y1={y}
              y2={y}
              stroke={CHART_COLORS.neutral200}
              strokeWidth={2}
            />
            <text
              x={PAD_L - 8}
              y={y + 5}
              fontSize={FONT}
              textAnchor="end"
              fill={CHART_COLORS.neutral500}
            >
              {fmt(t)}
            </text>
          </g>
        );
      })}
    </>
  );
}

/** Mini gráfico de barras: eje X = etiquetas, eje Y = valores. Barras
 *  planas brand-600. */
export function MiniBarChart({ puntos, formatValor, titulo }: ChartProps): JSX.Element {
  const { min, max } = rangoY(puntos);
  const innerW = CHART_W - PAD_L - PAD_R;
  const innerH = CHART_H - PAD_T - PAD_B;
  const n = puntos.length;
  const bandW = n > 0 ? innerW / n : 0;
  const barW = bandW * 0.62;
  const fmt = formatValor ?? ((v: number) => v.toLocaleString("es-CL"));

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      className="w-full h-auto"
      role="img"
      aria-label={titulo}
    >
      <GrillaY min={min} max={max} innerW={innerW} innerH={innerH} fmt={fmt} />
      {puntos.map((p, i) => {
        const h = ((p.valor - min) / (max - min || 1)) * innerH;
        const x = PAD_L + bandW * i + (bandW - barW) / 2;
        const y = PAD_T + innerH - h;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              fill={CHART_COLORS.brand600}
              rx={3}
            />
            <text
              x={x + barW / 2}
              y={CHART_H - PAD_B + 26}
              fontSize={FONT}
              textAnchor="middle"
              fill={CHART_COLORS.neutral600}
              fontWeight={500}
            >
              {p.label}
            </text>
            <title>
              {p.label}: {fmt(p.valor)}
            </title>
          </g>
        );
      })}
    </svg>
  );
}

/** Mini gráfico de línea con puntos visibles y área plana bajo la línea. */
export function MiniLineChart({ puntos, formatValor, titulo }: ChartProps): JSX.Element {
  const { min, max } = rangoY(puntos);
  const innerW = CHART_W - PAD_L - PAD_R;
  const innerH = CHART_H - PAD_T - PAD_B;
  const n = puntos.length;
  const fmt = formatValor ?? ((v: number) => v.toLocaleString("es-CL"));
  const stepX = n > 1 ? innerW / (n - 1) : 0;
  const color = CHART_COLORS.emerald600;

  const coords = puntos.map((p, i) => {
    const x = PAD_L + stepX * i;
    const y = PAD_T + innerH - ((p.valor - min) / (max - min || 1)) * innerH;
    return { x, y, p };
  });
  const path =
    coords.length === 0
      ? ""
      : "M " + coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" L ");
  const baseY = PAD_T + innerH;
  const areaPath =
    coords.length === 0
      ? ""
      : `${path} L ${coords[coords.length - 1]!.x.toFixed(1)},${baseY} L ${coords[0]!.x.toFixed(1)},${baseY} Z`;

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      className="w-full h-auto"
      role="img"
      aria-label={titulo}
    >
      <GrillaY min={min} max={max} innerW={innerW} innerH={innerH} fmt={fmt} />
      <path d={areaPath} fill={color} fillOpacity={0.1} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={4.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((c, i) => (
        <g key={i}>
          <circle cx={c.x} cy={c.y} r={7} fill="#fff" stroke={color} strokeWidth={4} />
          <text
            x={c.x}
            y={CHART_H - PAD_B + 26}
            fontSize={FONT}
            textAnchor="middle"
            fill={CHART_COLORS.neutral600}
            fontWeight={500}
          >
            {c.p.label}
          </text>
          <title>
            {c.p.label}: {fmt(c.p.valor)}
          </title>
        </g>
      ))}
    </svg>
  );
}
